import { Box, Typography, Stack, Divider, Button } from '@mui/material';

export interface DashboardHeaderStat {
  label: string;
  value: string | number;
}

export interface DashboardHeaderAction {
  label: string;
  icon: React.ComponentType<{ sx?: any }>;
  onClick: () => void;
  variant?: 'contained' | 'outlined' | 'text';
  disabled?: boolean;
  color?: 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info' | 'inherit';
}

export interface DashboardHeaderProps {
  icon: React.ComponentType<{ sx?: any }>;
  title: string;
  subtitle?: React.ReactNode;
  stats: DashboardHeaderStat[];
  action?: DashboardHeaderAction;
  actions?: DashboardHeaderAction[];
}

const DashboardHeader: React.FC<DashboardHeaderProps> = ({ icon: Icon, title, subtitle, stats, action, actions }) => {
  const allActions = actions ?? (action ? [action] : []);

  return (
    <Box sx={{ backgroundColor: '#ffffff', borderBottom: '2px solid #3b82f6', p: 1.5 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
        <Box display="flex" alignItems="center" gap={2}>
          <Icon sx={{ color: '#3b82f6', fontSize: 28 }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, color: '#1e293b', mb: 0.5 }}>
              {title}
            </Typography>
            {subtitle && (
              <Box sx={{ color: '#64748b' }}>
                {typeof subtitle === 'string' ? (
                  <Typography variant="body2" sx={{ color: '#64748b' }}>{subtitle}</Typography>
                ) : subtitle}
              </Box>
            )}
          </Box>
        </Box>

        <Stack direction="row" spacing={3} alignItems="center">
          {stats.map((stat, i) => (
            <Box key={stat.label} sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              {i > 0 && <Divider orientation="vertical" flexItem />}
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b' }}>
                  {stat.value}
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748b', textTransform: 'uppercase' }}>
                  {stat.label}
                </Typography>
              </Box>
            </Box>
          ))}
          {allActions.length > 0 && (
            <>
              {stats.length > 0 && <Divider orientation="vertical" flexItem />}
              <Stack direction="row" spacing={1.5}>
                {allActions.map((a) => (
                  <Button
                    key={a.label}
                    variant={a.variant ?? 'contained'}
                    color={a.color ?? 'primary'}
                    startIcon={<a.icon />}
                    onClick={a.onClick}
                    disabled={a.disabled}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      borderRadius: 2,
                    }}
                  >
                    {a.label}
                  </Button>
                ))}
              </Stack>
            </>
          )}
        </Stack>
      </Box>
    </Box>
  );
};

export default DashboardHeader;
